import { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Lock, Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Logo from '../components/Logo';
import { resetPassword } from '../services/api';
import { toast } from 'sonner';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setIsLoading(true);
    try {
      await resetPassword(token, email, password);
      setSuccess(true);
      toast.success('Password reset successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  if (!token || !email) {
    return (
      <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center px-4">
        <div className="text-center">
          <h2 className="text-white text-xl mb-4">Invalid Reset Link</h2>
          <p className="text-[#A1A1AA] mb-6">This password reset link is invalid or has expired.</p>
          <Link to="/admin" className="text-[#D4AF37] hover:underline">Go to Login</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B0C10] flex items-center justify-center px-4" data-testid="reset-password-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
        <div className="text-center mb-8 flex flex-col items-center">
          <Logo size="large" showText={false} />
          <div className="mt-4">
            <span className="text-2xl font-semibold text-[#D4AF37] heading-display">Mulberry</span>
            <span className="text-2xl font-light text-white heading-display ml-1">World</span>
          </div>
        </div>

        <div className="bg-[#1A1D24] border border-[#2A2F3A] rounded-xl p-8">
          {success ? (
            <div className="text-center py-4" data-testid="reset-success">
              <CheckCircle2 className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h2 className="text-white text-lg font-medium mb-2">Password Reset Complete</h2>
              <p className="text-[#A1A1AA] text-sm mb-6">Your password has been updated. You can now sign in with your new password.</p>
              <div className="flex gap-3 justify-center">
                <Link to="/admin"><Button className="btn-luxury" data-testid="go-admin-login">Admin Login</Button></Link>
                <Link to="/partner"><Button variant="outline" className="border-[#2A2F3A] text-[#A1A1AA] hover:text-white" data-testid="go-partner-login">Partner Login</Button></Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="reset-password-form">
              <div className="text-center mb-2">
                <Lock className="h-10 w-10 text-[#D4AF37] mx-auto mb-3" />
                <h2 className="text-white text-lg font-medium">Set New Password</h2>
                <p className="text-[#A1A1AA] text-sm mt-1">for <span className="text-white">{email}</span></p>
              </div>
              <div>
                <Label className="text-white mb-2 block">New Password</Label>
                <div className="relative">
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 8 characters"
                    className="pr-12 bg-[#0B0C10] border-[#2A2F3A] text-white placeholder:text-[#A1A1AA]"
                    required
                    minLength={8}
                    data-testid="new-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#A1A1AA] hover:text-white">
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div>
                <Label className="text-white mb-2 block">Confirm Password</Label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter password"
                  className="bg-[#0B0C10] border-[#2A2F3A] text-white placeholder:text-[#A1A1AA]"
                  required
                  minLength={8}
                  data-testid="confirm-password-input"
                />
              </div>
              <Button type="submit" className="w-full btn-luxury" disabled={isLoading} data-testid="reset-submit-btn">
                {isLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </form>
          )}
        </div>
      </motion.div>
    </div>
  );
}
